import { Navigate } from "react-router-dom";
import PageTitle from "../page-header/PageHeader";
import AddPerformanceFeedback from "./AddPerformanceFeedback";
import GetAllPerformanceFeedback from "./GetAllPerformanceFeedback";
import { Col, Row, Form } from "antd";
import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import { loadAllStaff } from "../../redux/rtk/features/user/userSlice";

const PerformanceFeedback = () => {
	const [form] = Form.useForm();
	const dispatch = useDispatch();
	const isLogged = Boolean(localStorage.getItem("isLogged"));
	const staffList = useSelector((state) => state.users?.list || []);

	const [feedbackList, setFeedbackList] = useState(() => {
		// Try to load from localStorage first
		const storedFeedback = localStorage.getItem("performanceFeedback");
		if (storedFeedback) {
			try {
				return JSON.parse(storedFeedback);
			} catch (error) {
				console.error("Error parsing feedback from localStorage:", error);
			}
		}
		// Default dummy data
		const now = dayjs();
		return [
			{
				id: 1,
				employeeId: 1,
				employeeName: "Pradeep Kumar",
				reviewerId: 2,
				reviewerName: "Manager A",
				feedbackDate: now.subtract(30, "day").format("YYYY-MM-DD"),
				reviewPeriod: "Monthly",
				overallRating: 4.5,
				technicalSkills: 4.5,
				communication: 4.0,
				teamwork: 5.0,
				problemSolving: 4.0,
				strengths: "Excellent technical skills, strong problem-solving abilities, great team player. Consistently delivers high-quality work on time.",
				areasForImprovement: "Could improve on time management for complex projects. Consider taking on more leadership responsibilities.",
				goals: "Complete advanced certification, lead a team project, improve documentation skills",
				comments: "Overall excellent performance. Keep up the great work!",
				status: "Completed",
			},
			{
				id: 2,
				employeeId: 2,
				employeeName: "Anshuman Singh",
				reviewerId: 1,
				reviewerName: "Manager B",
				feedbackDate: now.subtract(20, "day").format("YYYY-MM-DD"),
				reviewPeriod: "Quarterly",
				overallRating: 4.0,
				technicalSkills: 4.0,
				communication: 4.5,
				teamwork: 4.0,
				problemSolving: 3.5,
				strengths: "Strong communication skills, good at client interactions, reliable team member.",
				areasForImprovement: "Needs to improve technical depth. Should focus more on problem-solving approaches.",
				goals: "Complete technical training program, improve coding skills, contribute to technical documentation",
				comments: "Good progress this quarter. Continue focusing on technical development.",
				status: "Completed",
			},
			{
				id: 3,
				employeeId: 3,
				employeeName: "Priya Verma",
				reviewerId: 1,
				reviewerName: "Manager A",
				feedbackDate: now.subtract(10, "day").format("YYYY-MM-DD"),
				reviewPeriod: "Monthly",
				overallRating: 3.5,
				technicalSkills: 3.5,
				communication: 4.0,
				teamwork: 3.5,
				problemSolving: 3.0,
				strengths: "Good attention to detail, punctual, willing to learn new technologies.",
				areasForImprovement: "Needs improvement in problem-solving and independent work. Should take more initiative.",
				goals: "Improve problem-solving skills, complete assigned projects independently, enhance technical knowledge",
				comments: "Making steady progress. Focus on taking more ownership of tasks.",
				status: "Draft",
			},
			{
				id: 4,
				employeeId: 4,
				employeeName: "Rahul Das",
				reviewerId: 2,
				reviewerName: "Manager B",
				feedbackDate: now.subtract(5, "day").format("YYYY-MM-DD"),
				reviewPeriod: "Semi-Annual",
				overallRating: 4.5,
				technicalSkills: 5.0,
				communication: 4.0,
				teamwork: 4.5,
				problemSolving: 4.5,
				strengths: "Exceptional technical expertise, innovative solutions, mentors junior developers effectively.",
				areasForImprovement: "Could improve documentation of complex solutions. Sometimes takes on too much work.",
				goals: "Improve work-life balance, enhance documentation practices, mentor 2 junior developers",
				comments: "Outstanding performance. Consider for leadership role in next cycle.",
				status: "Completed",
			},
			{
				id: 5,
				employeeId: 5,
				employeeName: "Sara Smith",
				reviewerId: 1,
				reviewerName: "Manager A",
				feedbackDate: now.format("YYYY-MM-DD"),
				reviewPeriod: "Quarterly",
				overallRating: 3.0,
				technicalSkills: 3.0,
				communication: 3.5,
				teamwork: 3.0,
				problemSolving: 2.5,
				strengths: "Positive attitude, good at following instructions, collaborative spirit.",
				areasForImprovement: "Needs significant improvement in technical skills and problem-solving. Requires more training and support.",
				goals: "Complete basic technical training, improve coding fundamentals, seek help when stuck",
				comments: "Requires additional support and training. Schedule regular check-ins.",
				status: "Pending",
			},
		];
	});

	useEffect(() => {
		dispatch(loadAllStaff({ status: true }));
	}, [dispatch]);

	const mapEmployeeName = (employeeId) => {
		const employee = staffList.find((e) => e.id === employeeId);
		if (employee) {
			return (
				employee.EmployeeName ||
				`${employee.firstName || ""} ${employee.lastName || ""}`.trim() ||
				employee.UserName
			);
		}
		return "Unknown";
	};

	const handleSubmit = (values) => {
		const newId = feedbackList.length ? Math.max(...feedbackList.map((f) => f.id || 0)) + 1 : 1;
		const payload = {
			id: newId,
			employeeId: values.employeeId,
			reviewerId: values.reviewerId,
			employeeName: mapEmployeeName(values.employeeId),
			reviewerName: mapEmployeeName(values.reviewerId),
			feedbackDate: values.feedbackDate ? dayjs(values.feedbackDate).format("YYYY-MM-DD") : null,
			reviewPeriod: values.reviewPeriod,
			overallRating: values.overallRating || 0,
			technicalSkills: values.technicalSkills || 0,
			communication: values.communication || 0,
			teamwork: values.teamwork || 0,
			problemSolving: values.problemSolving || 0,
			strengths: values.strengths || "",
			areasForImprovement: values.areasForImprovement || "",
			goals: values.goals || "",
			comments: values.comments || "",
			status: "Completed",
		};
		setFeedbackList((prev) => [...prev, payload]);
		form.resetFields();
	};

	const handleEdit = (record) => {
		form.setFieldsValue({
			employeeId: record.employeeId,
			reviewerId: record.reviewerId,
			feedbackDate: record.feedbackDate ? dayjs(record.feedbackDate) : null,
			reviewPeriod: record.reviewPeriod,
			overallRating: record.overallRating,
			technicalSkills: record.technicalSkills,
			communication: record.communication,
			teamwork: record.teamwork,
			problemSolving: record.problemSolving,
			strengths: record.strengths,
			areasForImprovement: record.areasForImprovement,
			goals: record.goals,
			comments: record.comments,
		});
	};

	const refresh = () => {
		setFeedbackList((prev) => [...prev]);
	};

	// Sync feedback to localStorage whenever it changes
	useEffect(() => {
		localStorage.setItem("performanceFeedback", JSON.stringify(feedbackList));
	}, [feedbackList]);

	if (!isLogged) {
		return <Navigate to={"/admin/auth/login"} replace={true} />;
	}

	return (
		<div className="add-performance-feedback-page-wrapper">
			<PageTitle title="Back" />
			<Row gutter={16} className="add-performance-feedback-row">
				<Col xs={24} sm={24} md={10} lg={8} className="add-performance-feedback-form-col">
					<AddPerformanceFeedback form={form} onSubmit={handleSubmit} />
				</Col>
				<Col xs={24} sm={24} md={14} lg={16} className="add-performance-feedback-table-col">
					<GetAllPerformanceFeedback data={feedbackList} onEdit={handleEdit} onRefresh={refresh} />
				</Col>
			</Row>
		</div>
	);
};

export default PerformanceFeedback;

